import { Data } from '.generated/templates/Feature.Data.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import ImageWrapper from '../ImageWrapper/ImageWrapper';
import { classnames } from 'tailwindcss-classnames';
import { ButtonVariant } from '../Button/WithButton';
import Heading, { propsFromFields } from '../Heading';
import Button from '../Button';
import { OnLinkDataLayerPushProps, onCarouselCtaDataLayerPush } from 'lib/util/gtm-datalayer';
import ImageCaption from '../ImageCaption/ImageCaption';

export type HeroCarouselItemProps = Sitecore.Override.ItemEx &
  Data.Carousel.Fields.CarouselItem & {
    index: number;
    carouselName?: string;
    imageUrlOverride?: string;
  };

const HeroCarouselItem = ({
  fields,
  index,
  carouselName,
  imageUrlOverride,
}: HeroCarouselItemProps): JSX.Element => {
  if (!fields) {
    return <></>;
  }
  const headingProps = propsFromFields(fields);
  const leftSideImgColor = index % 2 === 0 ? 'bg-texture-dayglow' : 'bg-texture-plum';
  const rightSideImgColor = index % 2 === 0 ? 'bg-texture-plum' : 'bg-texture-dayglow';
  const cta2TypeField = fields.cta2Type.value;
  const cta2Type: ButtonVariant =
    cta2TypeField === 'Default' || cta2TypeField.trim().length === 0
      ? 'secondary'
      : (cta2TypeField.toLowerCase() as ButtonVariant);

  const sizedImage = {
    ...(fields.image ?? {}),
    value: {
      ...(fields.image?.value ?? {}),
      src: imageUrlOverride || fields.image?.value?.src,
      width: '988',
      height: '456',
    },
  };

  const handleClickPushGtm = (props: OnLinkDataLayerPushProps) => {
    onCarouselCtaDataLayerPush({
      pushEvent: props,
      index,
      carouselType: 'hero carousel',
      carouselText: carouselName,
    });
  };
  const priority = index === 0;
  return (
    <div className="hero-carousel-item">
      <div className={classnames('mx-4', 'md:mx-12', 'lg:mx-48', 'text-center')}>
        <div className={classnames('md:max-w-[720px]', 'mx-auto')}>
          <Heading
            {...headingProps}
            headingSize="xl"
            headingAlignment="center"
            descriptionClassName={classnames('md:text-[18px]', 'mb-4')}
          />
        </div>
        <div className={classnames('cta-container', 'mb-6')}>
          <Button linkField={fields.cta1} onGtmPush={handleClickPushGtm} />
          <Button linkField={fields.cta2} variant={cta2Type} onGtmPush={handleClickPushGtm} />
        </div>
      </div>
      <div className={classnames('grid', 'grid-cols-12')}>
        <div className={classnames('carousel-side-wrapper-left', 'col-span-1', 'md:col-span-2')}>
          <div className={`carousel-side-img ${leftSideImgColor}`} />
        </div>
        <div className={classnames('col-span-10', 'md:col-span-8')}>
          <ImageWrapper
            field={sizedImage}
            layout="responsive"
            className={classnames('rounded-full')}
            priority={priority}
          />
        </div>
        <div className={classnames('carousel-side-wrapper-right', 'col-span-1', 'md:col-span-2')}>
          <div className={`carousel-side-img ${rightSideImgColor}`} />
        </div>
        <div
          className={classnames(
            'row-start-2',
            'md:col-start-5',
            'col-span-12',
            'md:col-span-4',
            'text-center'
          )}
        >
          <ImageCaption
            imageCaption={fields.imageCaption}
            classNames={classnames('text-center', 'px-5', 'md:px-0')}
          />
        </div>
      </div>
    </div>
  );
};

export default HeroCarouselItem;
